import React, { Fragment, useMemo, useState } from 'react';
import { apiService } from '../../services/apiService';
import { CO_SELLER_PARTICIPANT_COPY, getSpeakerColor, Participant, ParticipantRole } from '../../config/participants';
import { Button } from '../ui/Button';
import {
  canMergeParticipants,
  formatFirstLine,
  formatMergedRow,
  formatTurns,
  formatUndoLabel,
  PARTICIPANT_MERGE_COPY,
} from '../../constants/participantMerge';
import {
  buildParticipantRows,
  displayNameOf,
  editsFrom,
  keepsASalesperson,
  MergeMap,
  ParticipantDraft,
  ParticipantRow,
  truncateQuote,
} from './participantRows';
import {
  canOfferMerge,
  combinedTurnCount,
  mergeRow,
  mergeTargets,
  resolveOwnerParticipantId,
  undoMerge,
} from './participantMerge';
import SameAsSelect, { SameAsTarget } from './SameAsSelect';
import { ColorDot, OwnMicBadge, RoleToggle, SAME_AS_SLOT_CLASS } from './ParticipantRowParts';
import {
  AutoMerge,
  AutoMergeLine,
  autoMergesInto,
  findAutoMerges,
  splitBack,
  splitEdits,
  SplitMap,
  splitsAddASalesperson,
  splitTurnsOutOf,
  undoSplit,
  updateSplit,
} from './participantSplit';
import { AutoMergedRow, SplitBackRow, splitRowLabel } from './AutoMergeRows';

/**
 * Who was on the call, and the one place to correct it after the call: each
 * person's name and role (customer | salesperson), and "Same as…" when one
 * person was split into two rows, or "Split back" when the call merged two
 * voices automatically (standalone desktop calls). Used on the Timeline tab and in Call
 * Insights → Details. The backend rewrites every line, alert and concern that
 * depends on the edit, and a role, name or merge change re-runs the analysis
 * (backend services/participantService.js); admins can edit any call, a user
 * only their own (the server enforces it). A rep who joined with their own mic
 * ("Own mic") is always a salesperson: their role toggle is disabled and they
 * are never merged into someone else. `readOnly` (a call the viewer joined as a
 * co-seller) shows the sheet with no edit control at all.
 */
export interface ParticipantsEditorProps {
  sessionId: string;
  transcripts: AutoMergeLine[] | undefined;
  participants: Participant[] | undefined;
  /** `session.metadata.source`: "Same as…" and "Split back" are offered only on standalone desktop calls. */
  sessionSource?: string;
  /** A call the viewer joined as a co-seller: no Edit participants, no Split back. */
  readOnly?: boolean;
  /** Called after a successful save; the parent reloads the session. */
  onSaved: (message: string) => void;
  onError: (message: string) => void;
}

const ROLE_LABEL: Record<ParticipantRole, string> = {
  [ParticipantRole.CUSTOMER]: 'Customer',
  [ParticipantRole.SALESPERSON]: 'Salesperson',
};

const ROLE_CHANGE_NOTE =
  'Changing a role updates every line and alert on this call, removes customer concerns attributed to a salesperson, and re-runs the call analysis.';
const NEEDS_SALESPERSON = 'At least one participant must be a salesperson.';

interface MergedRowProps {
  name: string;
  targetName: string;
  turnCount: number;
  color: string;
  disabled: boolean;
  onUndo: () => void;
}

/** A row marked "same as" another collapses to one line with Undo; its name/role controls are hidden. */
const MergedRow: React.FC<MergedRowProps> = ({ name, targetName, turnCount, color, disabled, onUndo }) => (
  <li className="flex items-start gap-3 py-1.5">
    <ColorDot color={color} />
    <span className="flex-grow min-w-0 py-1 truncate text-sm text-ink-muted">{formatMergedRow(name, targetName, turnCount)}</span>
    <button
      type="button"
      onClick={onUndo}
      disabled={disabled}
      aria-label={formatUndoLabel(name, targetName)}
      className="flex-shrink-0 py-1 text-sm font-medium text-signature hover:text-signature-strong hover:underline rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signature disabled:opacity-50"
    >
      {PARTICIPANT_MERGE_COPY.undo}
    </button>
  </li>
);

interface EditableRowProps {
  row: ParticipantRow;
  draft: ParticipantDraft | undefined;
  turnCount: number;
  color: string;
  saving: boolean;
  /** Null when the session cannot merge at all: no slot is rendered. Empty: slot kept, control hidden. */
  sameAsTargets: SameAsTarget[] | null;
  onChange: (change: Partial<ParticipantDraft>) => void;
  onMerge: (targetId: string) => void;
}

/** dot · [name / first line] · [Own mic] · Salesperson|Customer · N turns · Same as… — top-aligned. */
const EditableRow: React.FC<EditableRowProps> = ({ row, draft, turnCount, color, saving, sameAsTargets, onChange, onMerge }) => (
  <li className="flex items-start gap-3 py-1.5">
    <ColorDot color={color} />
    <div className="flex-grow min-w-0">
      <input
        aria-label={`Name of ${row.name}`}
        className="w-full text-sm border border-hairline rounded px-2 py-1"
        value={draft?.name ?? row.name}
        maxLength={100}
        disabled={saving}
        onChange={(event) => onChange({ name: event.target.value })}
      />
      {row.firstLine && (
        <p className="mt-0.5 px-0.5 truncate text-xs text-ink-muted" title={row.firstLine}>
          {formatFirstLine(truncateQuote(row.firstLine))}
        </p>
      )}
      {row.hasOwnMic && (
        <p className="mt-0.5 px-0.5 text-xs text-ink-muted">{CO_SELLER_PARTICIPANT_COPY.roleLocked}</p>
      )}
    </div>
    {row.hasOwnMic && <div className="py-1"><OwnMicBadge /></div>}
    <RoleToggle value={draft?.role ?? row.role} disabled={saving || row.hasOwnMic} personName={row.name}
                onChange={(next) => onChange({ role: next })} />
    <span className="w-16 flex-shrink-0 py-1 text-right text-xs text-ink-muted">{formatTurns(turnCount)}</span>
    {sameAsTargets && (
      <div className={SAME_AS_SLOT_CLASS} data-testid="same-as-slot">
        {sameAsTargets.length > 0 && (
          <SameAsSelect rowName={row.name} targets={sameAsTargets} disabled={saving} onSelect={onMerge} />
        )}
      </div>
    )}
  </li>
);

const ParticipantsEditor: React.FC<ParticipantsEditorProps> = ({
  sessionId,
  transcripts,
  participants,
  sessionSource,
  readOnly = false,
  onSaved,
  onError,
}) => {
  const rows = useMemo(() => buildParticipantRows(transcripts, participants), [transcripts, participants]);
  const [drafts, setDrafts] = useState<Record<string, ParticipantDraft> | null>(null);
  const [merges, setMerges] = useState<MergeMap>({});
  const [splits, setSplits] = useState<SplitMap>({});
  const autoMerges = useMemo(() => findAutoMerges(transcripts, rows, sessionSource), [transcripts, rows, sessionSource]);
  const [saving, setSaving] = useState(false);

  if (rows.length === 0) return null;

  const isEditing = drafts !== null;
  const rowEdits = isEditing ? editsFrom(rows, drafts, merges) : [];
  const edits = isEditing ? [...rowEdits, ...splitEdits(autoMerges, splits)] : [];
  // A split always sends its role, so only the existing rows' edits count as a role change.
  const rolesChanged = rowEdits.some((edit) => edit.role !== undefined);
  const hasMerge = rowEdits.some((edit) => edit.mergeInto !== undefined);
  // The salesperson rule only bites an edit that changes roles or merges, as on
  // the server: an old call saved without any salesperson can still be renamed.
  // A split-back row counts with the role chosen for it.
  const hasSalesperson =
    !isEditing || (!rolesChanged && !hasMerge) || keepsASalesperson(rows, drafts, merges) || splitsAddASalesperson(splits);
  const ownerParticipantId = resolveOwnerParticipantId(rows);
  const rowsById = new Map(rows.map((row) => [row.participantId, row]));

  const stopEditing = () => {
    setDrafts(null);
    setMerges({});
    setSplits({});
  };

  const isMergeable = canMergeParticipants(sessionSource);

  const sameAsTargetsFor = (row: ParticipantRow): SameAsTarget[] | null => {
    if (!isMergeable) return null;
    return canOfferMerge(rows, merges, row.participantId, ownerParticipantId)
      ? mergeTargets(rows, merges, row.participantId).map((target) => ({
          participantId: target.participantId,
          name: displayNameOf(target, drafts),
        }))
      : [];
  };

  const startEditing = () =>
    setDrafts(Object.fromEntries(rows.map((row) => [row.participantId, { name: row.name, role: row.role }])));

  // "Split back" is visible before editing starts; pressing it opens the editor with that row split.
  const handleSplitBack = (merge: AutoMerge) => {
    if (!isEditing) startEditing();
    setSplits((current) => splitBack(autoMerges, current, merge.sourceId));
  };

  const renderAutoMerge = (merge: AutoMerge, targetName: string) => {
    const splitDraft = splits[merge.sourceId];
    if (!isEditing || !splitDraft) {
      return (
        <AutoMergedRow key={`auto-${merge.sourceId}`} merge={merge} targetName={targetName} disabled={saving}
                       onSplitBack={readOnly ? undefined : () => handleSplitBack(merge)} />
      );
    }
    const color = getSpeakerColor({ speaker: splitDraft.role, participantId: merge.sourceId }, participants)?.border;
    return (
      <SplitBackRow key={`split-${merge.sourceId}`} merge={merge} draft={splitDraft} label={splitRowLabel(merge)}
                    color={color} saving={saving}
                    onChange={(change) => setSplits((current) => updateSplit(current, merge.sourceId, change))}
                    onUndo={() => setSplits((current) => undoSplit(current, merge.sourceId))} />
    );
  };

  const renderRow = (row: ParticipantRow) => {
    const draft = drafts?.[row.participantId];
    const role = draft?.role ?? row.role;
    const color = getSpeakerColor({ speaker: role, participantId: row.participantId }, participants)?.border;
    const mergeTarget = isEditing ? rowsById.get(merges[row.participantId]) : undefined;
    if (mergeTarget) {
      return (
        <MergedRow name={displayNameOf(row, drafts)} targetName={displayNameOf(mergeTarget, drafts)}
                   turnCount={row.turnCount} color={color} disabled={saving}
                   onUndo={() => setMerges((current) => undoMerge(current, row.participantId))} />
      );
    }
    if (isEditing) {
      return (
        <EditableRow row={row} draft={draft} color={color} saving={saving}
                     turnCount={combinedTurnCount(rows, merges, row.participantId) - splitTurnsOutOf(autoMerges, splits, row.participantId)}
                     sameAsTargets={sameAsTargetsFor(row)}
                     onChange={(change) => updateDraft(row, change)}
                     onMerge={(targetId) => setMerges((current) =>
                       mergeRow(rows, current, row.participantId, targetId, ownerParticipantId))} />
      );
    }
    return (
      <li className="flex items-center gap-3 py-1.5">
        <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <span className="flex-grow min-w-0 truncate text-sm text-ink">{row.name}</span>
        {row.hasOwnMic && <OwnMicBadge />}
        <span className="text-xs text-ink-muted">{ROLE_LABEL[row.role]}</span>
        <span className="w-16 text-right text-xs text-ink-muted">{formatTurns(row.turnCount)}</span>
      </li>
    );
  };

  const updateDraft = (row: ParticipantRow, change: Partial<ParticipantDraft>) =>
    setDrafts((current) => ({ ...(current ?? {}), [row.participantId]: { ...current![row.participantId], ...change } }));

  const handleSave = async () => {
    if (edits.length === 0) {
      stopEditing();
      return;
    }
    try {
      setSaving(true);
      const response = await apiService.put(`/admin/sessions/${sessionId}/participants`, { participants: edits });
      const reanalysing = Boolean((response as any)?.data?.reanalysisQueued);
      stopEditing();
      onSaved(reanalysing ? 'Participants updated. The call is being analysed again.' : 'Participants updated');
    } catch (err: any) {
      console.error('Failed to update participants:', err);
      onError(err?.response?.data?.message || 'Failed to update participants. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-hairline rounded-lg px-3 py-2" data-testid="participants-editor">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm font-semibold text-ink-secondary">Participants</div>
        {readOnly ? null : !isEditing ? (
          <Button type="button" variant="tertiary" size="sm" onClick={startEditing}
                  className="!text-signature hover:!text-signature-strong">
            Edit participants
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button type="button" variant="tertiary" size="sm" onClick={stopEditing} disabled={saving}>
              Cancel
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={handleSave}
                    disabled={saving || !hasSalesperson}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
      <ul className="divide-y divide-hairline">
        {rows.map((row) => (
          <Fragment key={row.participantId}>
            {renderRow(row)}
            {autoMergesInto(autoMerges, row.participantId).map((merge) => renderAutoMerge(merge, displayNameOf(row, drafts)))}
          </Fragment>
        ))}
      </ul>
      {isEditing && (rolesChanged || !hasSalesperson) && (
        <p className="mt-2 text-xs text-ink-muted">{hasSalesperson ? ROLE_CHANGE_NOTE : NEEDS_SALESPERSON}</p>
      )}
    </div>
  );
};

export default ParticipantsEditor;
